// interfaces to bridge functionality between Dactyl and other modules
// each must have an "init" function

import * as Elements from "./wpd/elements.js";
import * as Events from "./wpd/events.js";
import * as Functions from "./wpd/functions.js";
import * as KeyboardShortcuts from "./wpd/keyboard-shortcuts.js";
import * as Styles from "./wpd/styles.js";

import * as AcquireData from "./wpd/acquire-data.js";
import * as AlignAxes from "./wpd/align-axes.js";
import * as Colors from "./wpd/colors.js";
import * as CustomFunctions from "./wpd/custom-functions.js";
import * as Dataset from "./wpd/dataset.js";
import * as DataSeriesManagement from "./wpd/data-series-management.js";
import * as FileManager from "./wpd/file-manager.js";
import * as GraphicsWidget from "./wpd/graphics-widget.js";
import * as ImageManager from "./wpd/image-manager.js";
import * as LayoutManager from "./wpd/layout-manager.js";
import * as Repainters from "./wpd/repainters.js";
import * as PageManager from "./wpd/page-manager.js";
import * as PdfManager from "./wpd/pdf-manager.js";
import * as PlotData from "./wpd/plot-data.js";
import * as PointGroups from "./wpd/point-groups.js";
import * as TableAxis from "./wpd/table-axis.js";
import * as TagGroups from "./wpd/tag-groups.js";
import * as Tools from "./wpd/tools.js";
import * as Tree from "./wpd/tree.js";
import * as Utils from "./wpd/utils.js";
import * as XYGraphAxis from "./wpd/xy-graph-axis/xy-graph-axis.js";
import * as BarGraphAxis from "./wpd/bar-graph-axis/bar-graph-axis.js";

const interfaces = {
	wpd: (() => {
		let wpdDocument = null;
		let wpd = null;

		const options = {};

		// `wpd`/`wpdDocument` are module-level singleton state: init() rebinds
		// them to the newest DataEntry iframe and dispose() nulls them on
		// unmount. Between a dispose and the next init there is no document
		// that belongs to the current page, so any access must fail loudly
		// instead of silently serializing a previous extraction's document
		// (the Jul 2026 cross-SDE save corruption incident).
		function requireWpd() {
			if (!wpd) {
				throw new Error(
					"WPD interface is not bound to an extraction (not initialized yet, or already disposed)",
				);
			}
			return wpd;
		}

		function requireWpdDocument() {
			if (!wpdDocument) {
				throw new Error(
					"WPD document is not bound to an extraction (not initialized yet, or already disposed)",
				);
			}
			return wpdDocument;
		}

		// Releases the singleton's hold on the outgoing extraction so a save
		// fired before the next init() throws instead of writing stale data.
		// Called from DataEntry's beforeUnmount().
		function dispose() {
			wpd = null;
			wpdDocument = null;
		}

		/**
		 * Sets up customizations for Dactyl.
		 * @param {Object}       wpdWindow   - Main window object
		 * @param {VueComponent} dataEntryVM - DataEntry Vue instance
		 */
		function init(wpdWindow, dataEntryVM) {
			wpdDocument = wpdWindow.document;
			wpd = wpdWindow.wpd;

			// With Vue3 upgrade, the default logging was leading to 404s in "/modules/wpd/app/log"
			wpd.log = function () {
				return;
			};

			// utils
			wpd.CompositeKeyUtils = dataEntryVM.compositeKeyUtils;

			// apply UI changes
			Elements.apply(wpd, wpdDocument, dataEntryVM);
			Styles.apply(wpdDocument);

			// initialize custom logic
			AcquireData.init(wpd, dataEntryVM);
			AlignAxes.init(wpd, wpdDocument, dataEntryVM);
			Colors.init(wpd);
			CustomFunctions.init(wpd, wpdWindow, wpdDocument, dataEntryVM);
			Dataset.init(wpd, dataEntryVM);
			DataSeriesManagement.init(wpd, wpdDocument, dataEntryVM);
			FileManager.init(wpd, dataEntryVM);
			GraphicsWidget.init(wpd, wpdDocument, dataEntryVM);
			ImageManager.init(wpd, wpdWindow, wpdDocument, dataEntryVM);
			LayoutManager.init(wpd, wpdWindow, wpdDocument, dataEntryVM);
			Repainters.init(wpd, dataEntryVM);
			PageManager.init(wpd, wpdDocument, dataEntryVM);
			PdfManager.init(wpd, wpdWindow, wpdDocument, dataEntryVM);
			PlotData.init(wpd, dataEntryVM);
			PointGroups.init(wpd, wpdDocument, dataEntryVM);
			TagGroups.init(wpd, wpdWindow, wpdDocument, dataEntryVM);
			TableAxis.init(wpd, wpdWindow, wpdDocument, dataEntryVM, options);
			Tools.init(wpd, wpdDocument, wpdWindow, dataEntryVM);
			Tree.init(wpd, wpdDocument, dataEntryVM);
			Utils.init(wpd);
			XYGraphAxis.init(wpd, wpdDocument, dataEntryVM);
			BarGraphAxis.init(wpd, wpdDocument, dataEntryVM);

			// apply events
			Events.apply(wpd, wpdWindow, wpdDocument, dataEntryVM);

			// apply custom keyboard shortcuts
			KeyboardShortcuts.apply(wpd, wpdDocument);

			// overwrite WPD init workflow
			wpd.loadDefaultImage = () => {};
			wpd.initApp();
		}

		return {
			disableUndo: () => Functions.disableUndo(requireWpdDocument()),
			disableRedo: () => Functions.disableRedo(requireWpdDocument()),
			enableUndo: () => Functions.enableUndo(requireWpdDocument()),
			enableRedo: () => Functions.enableRedo(requireWpdDocument()),
			dispose: dispose,
			init: init,
			isBound: () => wpd !== null,
			getBounds: () => Functions.getBounds(requireWpdDocument()),
			getCurrentLocationInfo: () =>
				Functions.getCurrentLocationInfo(requireWpd()),
			getSelectedPaths: () => Functions.getSelectedPaths(requireWpd()),
			inject: (dataEntryVM) =>
				Functions.inject(requireWpd(), requireWpdDocument(), dataEntryVM),
			loadFiles: (files) => Functions.loadFiles(requireWpd(), files),
			loadJSON: async (dataEntryVM, json, dataPointUuid) =>
				await Functions.loadJSON(
					requireWpd(),
					dataEntryVM,
					json,
					dataPointUuid,
				),
			navigateToDataPoint: async (dataPointUuid) =>
				await Functions.navigateToDataPoint(requireWpd(), dataPointUuid),
			saveJSON: () => Functions.saveJSON(requireWpd()),
			selectPath: (rootPath, axisPath) =>
				Functions.selectPath(requireWpd(), rootPath, axisPath),
			setMetadata: (payload) => Functions.setMetadata(requireWpd(), payload),
			setOptions: (newOptions) => Functions.setOptions(options, newOptions),
		};
	})(),
};

export default interfaces;
